<template>
  <div class="mobile-menu-sheet ml-auto">
    <button
      type="button"
      class="mobile-menu-sheet__toggle"
      aria-label="Open navigation menu"
      aria-controls="mobile-menu-sheet-dialog"
      :aria-expanded="String(isOpen)"
      @click="openMenu"
    >
      <i class="fa fa-bars" aria-hidden="true"></i>
    </button>

    <transition name="mobile-menu-sheet" @after-leave="handleAfterLeave">
      <div
        v-if="isOpen"
        class="mobile-menu-sheet__backdrop"
        role="presentation"
        @click.self="closeMenu"
      >
        <section
          id="mobile-menu-sheet-dialog"
          ref="dialog"
          class="mobile-menu-sheet__dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="mobile-menu-sheet-title"
          tabindex="-1"
        >
          <div class="mobile-menu-sheet__handle" aria-hidden="true"></div>

          <header class="mobile-menu-sheet__header">
            <div>
              <span class="mobile-menu-sheet__eyebrow">Lymphomap</span>
              <h2 id="mobile-menu-sheet-title" class="mobile-menu-sheet__title">App menu</h2>
            </div>

            <button
              ref="closeButton"
              type="button"
              class="mobile-menu-sheet__close"
              aria-label="Close navigation menu"
              @click="closeMenu"
            >
              <i class="fa fa-times" aria-hidden="true"></i>
            </button>
          </header>

          <div class="mobile-menu-sheet__categories" aria-label="Navigation categories">
            <button
              v-for="group in menuGroups"
              :key="group.id"
              type="button"
              class="mobile-menu-sheet__category"
              :class="{ 'mobile-menu-sheet__category--active': activeGroupId === group.id && !searchQuery }"
              :aria-pressed="String(activeGroupId === group.id && !searchQuery)"
              @click="selectGroup(group.id)"
            >
              <i :class="['fa', group.icon]" aria-hidden="true"></i>
              {{ group.label }}
            </button>
          </div>

          <div class="mobile-menu-sheet__body">
            <div class="mobile-menu-sheet__results-heading">
              <div>
                <h3>{{ resultsTitle }}</h3>
              </div>
              <small aria-live="polite">
                {{ filteredItems.length }} {{ filteredItems.length === 1 ? 'item' : 'items' }}
              </small>
            </div>

            <ul v-if="filteredItems.length" class="mobile-menu-sheet__grid">
              <li v-for="item in filteredItems" :key="`${item.groupId}-${item.id}`">
                <button
                  v-if="item.action === 'logout'"
                  type="button"
                  class="mobile-menu-sheet__item mobile-menu-sheet__item--danger"
                  :disabled="isLoggingOut"
                  @click="logout"
                >
                  <span class="mobile-menu-sheet__item-icon" aria-hidden="true">
                    <i :class="['fa', isLoggingOut ? 'fa-spinner fa-spin' : item.icon]"></i>
                  </span>
                  <span class="mobile-menu-sheet__item-copy">
                    <strong>{{ isLoggingOut ? 'Logging out…' : item.label }}</strong>
                    <small v-if="searchQuery">{{ item.groupLabel }}</small>
                  </span>
                </button>

                <router-link
                  v-else
                  :to="item.to"
                  class="mobile-menu-sheet__item"
                  active-class="mobile-menu-sheet__item--active"
                  :exact="Boolean(item.exact)"
                  @click.native="closeMenu"
                >
                  <span class="mobile-menu-sheet__item-icon" aria-hidden="true">
                    <i :class="['fa', item.icon]"></i>
                  </span>
                  <span class="mobile-menu-sheet__item-copy">
                    <strong>{{ item.label }}</strong>
                    <small v-if="searchQuery">{{ item.groupLabel }}</small>
                  </span>
                  <span
                    v-if="item.badge && notificationCount"
                    class="mobile-menu-sheet__badge"
                    :aria-label="`${notificationCount} unread notifications`"
                  >
                    {{ notificationBadgeLabel }}
                  </span>
                </router-link>
              </li>
            </ul>

            <div v-else class="mobile-menu-sheet__empty" role="status">
              <i class="fa fa-search" aria-hidden="true"></i>
              <strong>No menu item found</strong>
              <span>Try another search term.</span>
            </div>

            <div class="mobile-menu-sheet__chatbot">
              <tukivita></tukivita>
            </div>
          </div>
        </section>
      </div>
    </transition>
  </div>
</template>

<script>
import toast from '@/services/toast';
import tukivita from '@/components/private/ai-chatbot/';

const BODY_SCROLL_LOCK_CLASS = 'no-scroll';
const DEFAULT_GROUP_ID = 'main';
const DESKTOP_BREAKPOINT = 768;

const MENU_GROUPS = Object.freeze([
  {
    id: 'main',
    label: 'Main',
    description: 'Workspace navigation',
    icon: 'fa-home',
    items: Object.freeze([
      { id: 'home', label: 'Home', icon: 'fa-home', to: '/home/', exact: true },
      { id: 'workspace', label: 'Workspace', icon: 'fa-briefcase', to: { name: 'teamWorkspace' } },
      { id: 'meetings', label: 'Meetings', icon: 'fa-video-camera', to: '/meeting/' },
      { id: 'webinar', label: 'Webinar', icon: 'fa-television', to: '/webinar/' },
      { id: 'members', label: 'Members', icon: 'fa-users', to: '/members/' },
      { id: 'payment', label: 'Payment', icon: 'fa-credit-card', to: { name: 'paymentHistory' } },
      {
        id: 'notifications',
        label: 'Notifications',
        icon: 'fa-bell',
        to: { name: 'globalNotification' },
        badge: true
      },
      { id: 'logout', label: 'Logout', icon: 'fa-sign-out', action: 'logout' }
    ])
  },
  {
    id: 'resources',
    label: 'Resources',
    description: 'Knowledge and records',
    icon: 'fa-wrench',
    items: Object.freeze([
      { id: 'blogs', label: 'Blogs', icon: 'fa-pencil-square-o', to: { name: 'mobileViewBlog' } },
      {
        id: 'clinical-records',
        label: 'Clinical Records',
        icon: 'fa-file-text-o',
        to: { name: 'comingSoon' }
      },
      { id: 'awerack', label: 'AweRack', icon: 'fa-folder-open', to: { name: 'paperPiles' } }
    ])
  },
  {
    id: 'products',
    label: 'Products',
    description: 'Lymphomap products',
    icon: 'fa-th-large',
    items: Object.freeze([
      { id: 'ai', label: 'AI', icon: 'fa-cogs', to: '/ai/' },
      { id: 'data', label: 'Data', icon: 'fa-database', to: '/data/' },
      { id: 'vista', label: 'Vista', icon: 'fa-eye', to: { name: 'vistaConference' } }
    ])
  }
]);

export default {
  name: 'MobileMenuBar',
  components: {
    tukivita
  },
  props: {
    notificationBadgeINFO: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      menuGroups: MENU_GROUPS,
      isOpen: false,
      activeGroupId: DEFAULT_GROUP_ID,
      searchQuery: '',
      isLoggingOut: false,
      isBodyScrollLocked: false,
      bodyHadScrollLockClass: false,
      previousBodyOverflow: '',
      lastFocusedElement: null
    };
  },
  computed: {
    activeGroup() {
      return this.menuGroups.find((group) => group.id === this.activeGroupId) || this.menuGroups[0];
    },
    allMenuItems() {
      return this.menuGroups.reduce((items, group) => {
        const groupItems = group.items.map((item) => Object.assign({}, item, {
          groupId: group.id,
          groupLabel: group.label
        }));
        return items.concat(groupItems);
      }, []);
    },
    filteredItems() {
      const query = this.searchQuery.toLowerCase();
      if (query) {
        return this.allMenuItems.filter((item) => (
          item.label.toLowerCase().indexOf(query) !== -1 ||
          item.groupLabel.toLowerCase().indexOf(query) !== -1
        ));
      }

      return this.activeGroup.items.map((item) => Object.assign({}, item, {
        groupId: this.activeGroup.id,
        groupLabel: this.activeGroup.label
      }));
    },
    resultsEyebrow() {
      return this.searchQuery ? 'Search results' : this.activeGroup.description;
    },
    resultsTitle() {
      return this.searchQuery ? `Results for “${this.searchQuery}”` : this.activeGroup.label;
    },
    notificationCount() {
      const count = this.notificationBadgeINFO && this.notificationBadgeINFO.global_notification_count;
      const normalizedCount = Number(count);
      return Number.isFinite(normalizedCount) && normalizedCount > 0 ? normalizedCount : 0;
    },
    notificationBadgeLabel() {
      return this.notificationCount > 99 ? '99+' : String(this.notificationCount);
    }
  },
  watch: {
    '$route.fullPath'() {
      this.closeMenu();
    }
  },
  mounted() {
    document.addEventListener('keydown', this.handleKeydown);
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeydown);
    window.removeEventListener('resize', this.handleResize);
    this.unlockBodyScroll();
  },
  methods: {
    openMenu() {
      if (this.isOpen) {
        return;
      }

      this.lastFocusedElement = document.activeElement;
      this.activeGroupId = this.findActiveGroupId();
      this.searchQuery = '';
      this.isOpen = true;
      this.lockBodyScroll();

      this.$nextTick(() => {
        if (this.$refs.searchInput) {
          this.$refs.searchInput.focus();
        }
      });
    },
    closeMenu() {
      if (this.isOpen) {
        this.isOpen = false;
      }
    },
    handleAfterLeave() {
      this.searchQuery = '';
      this.activeGroupId = DEFAULT_GROUP_ID;
      this.unlockBodyScroll();
      this.restoreFocus();
    },
    selectGroup(groupId) {
      if (this.menuGroups.some((group) => group.id === groupId)) {
        this.activeGroupId = groupId;
        this.searchQuery = '';
      }
    },
    clearSearch() {
      this.searchQuery = '';
      this.$nextTick(() => {
        if (this.$refs.searchInput) {
          this.$refs.searchInput.focus();
        }
      });
    },
    findActiveGroupId() {
      const currentPath = this.normalizePath(this.$route.path);

      for (let groupIndex = 0; groupIndex < this.menuGroups.length; groupIndex += 1) {
        const group = this.menuGroups[groupIndex];
        for (let itemIndex = 0; itemIndex < group.items.length; itemIndex += 1) {
          const item = group.items[itemIndex];
          if (!item.to) {
            continue;
          }

          const targetPath = this.normalizePath(this.$router.resolve(item.to).route.path);
          if (
            currentPath === targetPath ||
            (targetPath !== '/' && currentPath.indexOf(`${targetPath}/`) === 0)
          ) {
            return group.id;
          }
        }
      }

      return DEFAULT_GROUP_ID;
    },
    normalizePath(path) {
      if (!path || path === '/') {
        return '/';
      }
      return path.replace(/\/+$/, '');
    },
    handleKeydown(event) {
      if (!this.isOpen) {
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeMenu();
        return;
      }
      if (event.key === 'Tab') {
        this.trapFocus(event);
      }
    },
    trapFocus(event) {
      const dialog = this.$refs.dialog;
      if (!dialog) {
        return;
      }

      const focusableElements = [].slice.call(dialog.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ));
      if (!focusableElements.length) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    },
    handleResize() {
      if (this.isOpen && window.innerWidth >= DESKTOP_BREAKPOINT) {
        this.closeMenu();
      }
    },
    lockBodyScroll() {
      if (this.isBodyScrollLocked) {
        return;
      }
      this.bodyHadScrollLockClass = document.body.classList.contains(BODY_SCROLL_LOCK_CLASS);
      this.previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      document.body.classList.add(BODY_SCROLL_LOCK_CLASS);
      this.isBodyScrollLocked = true;
    },
    unlockBodyScroll() {
      if (!this.isBodyScrollLocked) {
        return;
      }
      document.body.style.overflow = this.previousBodyOverflow;
      if (!this.bodyHadScrollLockClass) {
        document.body.classList.remove(BODY_SCROLL_LOCK_CLASS);
      }
      this.previousBodyOverflow = '';
      this.bodyHadScrollLockClass = false;
      this.isBodyScrollLocked = false;
    },
    restoreFocus() {
      if (
        this.lastFocusedElement &&
        document.body.contains(this.lastFocusedElement) &&
        typeof this.lastFocusedElement.focus === 'function'
      ) {
        this.lastFocusedElement.focus();
      }
      this.lastFocusedElement = null;
    },
    notifyLogoutAcrossTabs() {
      const channel = window.authChannel;
      if (
        'BroadcastChannel' in window &&
        channel &&
        typeof channel.postMessage === 'function'
      ) {
        channel.postMessage({ cmd: 'logout' });
      }
    },
    async logout() {
      if (this.isLoggingOut) {
        return;
      }

      this.isLoggingOut = true;
      try {
        await this.$store.dispatch('logoutAction');
        this.notifyLogoutAcrossTabs();
        this.closeMenu();
      } catch (error) {
        toast.error(this.getErrorMessage(error, 'Unable to log out. Please try again.'));
      } finally {
        this.isLoggingOut = false;
      }
    },
    getErrorMessage(error, fallbackMessage) {
      const responseData = error && error.response && error.response.data;
      const directData = error && error.data;
      const data = responseData || directData || {};
      return data.user_msg || data.msg || data.message || fallbackMessage;
    }
  }
};
</script>

<style lang="scss" scoped>
$color-text: #f5f5f5;
$color-muted: #a8a8a8;
$color-accent: #ffd400;
$color-danger: #ff6259;
$color-border: rgba(255, 255, 255, 0.11);
$focus-ring: 0 0 0 3px rgba(255, 212, 0, 0.22);

.mobile-menu-sheet {
  &__toggle {
    display: inline-flex;
    width: 42px;
    height: 42px;
    align-items: center;
    justify-content: center;
    padding: 0;
    color: $color-accent;
    font-size: 22px;
    background: rgba(0, 0, 0, 0.5);
    border: 1px solid rgba(255, 212, 0, 0.26);
    border-radius: 50%;
    cursor: pointer;

    &:hover,
    &:focus-visible {
      color: #111111;
      background: $color-accent;
      border-color: $color-accent;
      outline: 0;
    }

    &:focus-visible {
      box-shadow: $focus-ring;
    }
  }

  &__backdrop {
    position: fixed;
    top: 0;
    right: 1px;
    bottom: 0;
    left: 0;
    z-index: 10001;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    background: rgba(0, 0, 0, 0.7);
    -webkit-backdrop-filter: blur(4px);
    backdrop-filter: blur(4px);
  }

  &__dialog {
    display: flex;
    width: 100%;
    max-width: 620px;
    height: 82vh;
    max-height: 760px;
    flex-direction: column;
    overflow: hidden;
    color: $color-text;
    background: rgba(7, 7, 7, 0.98);
    border: 1px solid rgba(255, 255, 255, 0.13);
    border-bottom: 0;
    border-radius: 20px 20px 0 0;
    box-shadow: 0 -18px 45px rgba(0, 0, 0, 0.42);
  }

  &__handle {
    width: 42px;
    height: 4px;
    flex: 0 0 4px;
    margin: 9px auto 2px;
    background: rgba(255, 255, 255, 0.22);
    border-radius: 999px;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
    padding: 8px 16px 10px;
  }

  &__eyebrow {
    display: block;
    margin-bottom: 1px;
    color: $color-accent;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
  }

  &__title {
    margin: 0;
    color: $color-text;
    font-size: 20px;
    font-weight: 700;
  }

  &__close {
    display: inline-flex;
    width: 36px;
    height: 36px;
    align-items: center;
    justify-content: center;
    padding: 0;
    color: $color-muted;
    background: rgba(255, 255, 255, 0.055);
    border: 1px solid $color-border;
    border-radius: 50%;
    cursor: pointer;

    &:hover,
    &:focus-visible {
      color: $color-danger;
      background: rgba(255, 98, 89, 0.08);
      border-color: rgba(255, 98, 89, 0.3);
      outline: 0;
    }

    &:focus-visible {
      box-shadow: $focus-ring;
    }
  }

  &__categories {
    display: flex;
    gap: 7px;
    padding: 0 16px 12px;
    overflow-x: auto;
    border-bottom: 1px solid $color-border;
  }

  &__category {
    display: inline-flex;
    min-height: 34px;
    flex: 0 0 auto;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    color: #8d8d8d;
    font-family: inherit;
    font-size: 11px;
    font-weight: 700;
    background: rgba(255, 255, 255, 0.035);
    border: 1px solid rgba(255, 255, 255, 0.07);
    border-radius: 999px;
    cursor: pointer;

    &:hover,
    &:focus-visible {
      color: $color-text;
      outline: 0;
    }

    &:focus-visible {
      box-shadow: $focus-ring;
    }

    &--active {
      color: #111111;
      background: $color-accent;
      border-color: $color-accent;
      &:hover { color: #111111; }
    }
  }

  &__body {
    flex: 1;
    padding: 14px 16px calc(18px + env(safe-area-inset-bottom));
    overflow-y: auto;
    overscroll-behavior: contain;
  }

  &__results-heading {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 14px;
    margin-bottom: 10px;

    span {
      color: #777777;
      font-size: 9px;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
    }

    h3 {
      margin: 1px 0 0;
      color: $color-text;
      font-size: 15px;
      font-weight: 700;
    }

    small {
      color: #737373;
      font-size: 10px;
    }
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  &__item {
    position: relative;
    display: flex;
    width: 100%;
    min-height: 70px;
    align-items: center;
    gap: 10px;
    padding: 10px;
    color: #dedede;
    font-family: inherit;
    text-align: left;
    text-decoration: none;
    background: rgba(255, 255, 255, 0.035);
    border: 1px solid rgba(255, 255, 255, 0.075);
    border-radius: 10px;
    cursor: pointer;

    &:hover,
    &:focus-visible,
    &--active {
      color: $color-text;
      text-decoration: none;
      background: rgba(255, 255, 255, 0.055);
      border-color: rgba(255, 212, 0, 0.2);
      outline: 0;
    }

    &:focus-visible {
      box-shadow: $focus-ring;
    }

    &--active {
      color: $color-accent;
      background: rgba(255, 212, 0, 0.07);
      border-color: rgba(255, 212, 0, 0.22);
    }

    &--danger:hover,
    &--danger:focus-visible {
      color: $color-danger;
      background: rgba(255, 98, 89, 0.06);
      border-color: rgba(255, 98, 89, 0.16);
    }

    &:disabled {
      cursor: wait;
      opacity: 0.58;
    }
  }

  &__item-icon {
    display: inline-flex;
    width: 38px;
    height: 38px;
    flex: 0 0 38px;
    align-items: center;
    justify-content: center;
    color: $color-accent;
    font-size: 16px;
    background: rgba(255, 212, 0, 0.055);
    border-radius: 9px;
  }

  &__item--danger &__item-icon {
    color: $color-danger;
    background: rgba(255, 98, 89, 0.055);
  }

  &__item-copy {
    display: flex;
    min-width: 0;
    flex: 1;
    flex-direction: column;

    strong,
    small {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    strong {
      color: inherit;
      font-size: 13px;
      font-weight: 700;
    }

    small {
      color: #747474;
      font-size: 9px;
    }
  }

  &__badge {
    position: absolute;
    top: 7px;
    right: 7px;
    min-width: 22px;
    padding: 3px 5px;
    color: #ffffff;
    font-size: 9px;
    font-weight: 800;
    line-height: 1.1;
    text-align: center;
    background: #b41818;
    border-radius: 999px;
  }

  &__empty {
    display: flex;
    min-height: 180px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #737373;
    text-align: center;
    border: 1px dashed rgba(255, 255, 255, 0.1);
    border-radius: 12px;

    .fa {
      margin-bottom: 8px;
      font-size: 22px;
    }

    strong {
      color: #bdbdbd;
      font-size: 13px;
    }

    span {
      font-size: 11px;
    }
  }

  &__chatbot {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    margin-top: 14px;
  }

  &__sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
}

.mobile-menu-sheet-enter-active,
.mobile-menu-sheet-leave-active {
  transition: opacity 190ms ease;

  .mobile-menu-sheet__dialog {
    transition: transform 230ms ease;
  }
}

.mobile-menu-sheet-enter,
.mobile-menu-sheet-leave-to {
  opacity: 0;

  .mobile-menu-sheet__dialog {
    transform: translateY(100%);
  }
}

@media only screen and (max-width: 340px) {
  .mobile-menu-sheet__grid {
    grid-template-columns: 1fr;
  }
}

@media only screen and (orientation: landscape) and (max-height: 520px) {
  .mobile-menu-sheet__dialog {
    height: 94vh;
  }
}

@media (prefers-reduced-motion: reduce) {
  .mobile-menu-sheet *,
  .mobile-menu-sheet-enter-active,
  .mobile-menu-sheet-leave-active,
  .mobile-menu-sheet-enter-active .mobile-menu-sheet__dialog,
  .mobile-menu-sheet-leave-active .mobile-menu-sheet__dialog {
    transition-duration: 0.01ms !important;
  }
}
</style>